import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/utils/api";
import { Target, AlertCircle, CheckCircle, Eye, EyeOff, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Token inválido o expirado. Solicita un nuevo enlace.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#080C14] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-red-400 mb-4">Enlace inválido. Solicita uno nuevo.</p>
          <Link to="/forgot-password" className="text-emerald-400 text-sm hover:underline">
            Recuperar contraseña
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080C14] flex flex-col items-center justify-center px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-500/15 border border-emerald-500/30 rounded-2xl mb-4">
            <Target className="w-7 h-7 text-emerald-400" />
          </div>
          <h1 className="font-outfit text-3xl font-bold text-slate-50 tracking-tight">RadarLit</h1>
          <p className="text-slate-400 text-sm mt-1">Tu radar en la contratación pública · SECOP II</p>
        </div>

        <div className="bg-slate-900 border border-slate-700/50 rounded-2xl p-8 shadow-2xl">
          {success ? (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-500/15 border border-emerald-500/30 rounded-2xl mb-4">
                <CheckCircle className="w-7 h-7 text-emerald-400" />
              </div>
              <h2 className="text-slate-200 font-semibold text-lg mb-3">¡Contraseña actualizada!</h2>
              <p className="text-slate-400 text-sm mb-6">
                Tu contraseña fue restablecida correctamente. Serás redirigido al inicio de sesión en 3 segundos...
              </p>
              <Link to="/login" className="inline-flex items-center gap-2 text-slate-400 hover:text-emerald-400 text-sm transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Ir al inicio de sesión
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-slate-200 font-semibold text-lg mb-2">Nueva contraseña</h2>
              <p className="text-slate-400 text-sm mb-6">Ingresa y confirma tu nueva contraseña.</p>

              {error && (
                <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg px-4 py-3 mb-5">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-slate-400 text-xs font-medium mb-1.5 block">Nueva contraseña</label>
                  <div className="relative">
                    <input
                      data-testid="reset-password-new"
                      type={showPwd ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      className="w-full bg-slate-800 border border-slate-700 text-slate-100 rounded-lg px-4 py-2.5 pr-10 text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all"
                      placeholder="Mínimo 8 caracteres"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-slate-400 text-xs font-medium mb-1.5 block">Confirmar contraseña</label>
                  <input
                    data-testid="reset-password-confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="w-full bg-slate-800 border border-slate-700 text-slate-100 rounded-lg px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all"
                    placeholder="Repite la contraseña"
                  />
                </div>

                <button
                  data-testid="reset-password-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary rounded-lg py-2.5 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                >
                  {loading ? "Actualizando..." : "Establecer nueva contraseña"}
                </button>
              </form>

              <div className="mt-4 text-center">
                <Link to="/login" className="inline-flex items-center gap-1.5 text-slate-500 hover:text-emerald-400 text-xs transition-colors">
                  <ArrowLeft className="w-3 h-3" />
                  Volver al inicio de sesión
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
